<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = withDefaults(defineProps<{
  page: number
  pageSize: number
  total: number
  pageSizeOptions?: number[]
}>(), {
  pageSizeOptions: () => [5, 10, 25, 50],
})

const emit = defineEmits<{
  'update:page': [page: number]
  'update:pageSize': [pageSize: number]
}>()

const totalPages = computed(() =>
  Math.max(1, Math.ceil(props.total / props.pageSize)),
)
const firstRecord = computed(() =>
  props.total ? (props.page - 1) * props.pageSize + 1 : 0,
)
const lastRecord = computed(() =>
  Math.min(props.page * props.pageSize, props.total),
)
const pages = computed(() => {
  const start = Math.max(1, Math.min(props.page - 2, totalPages.value - 4))
  const end = Math.min(totalPages.value, start + 4)
  return Array.from({ length: end - start + 1 }, (_, index) => start + index)
})

function setPageSize(event: Event): void {
  emit('update:pageSize', Number((event.target as HTMLSelectElement).value))
  emit('update:page', 1)
}
</script>

<template>
  <nav class="pagination" :aria-label="t('engineering.pagination.aria')">
    <span class="pagination__count">
      {{ t('engineering.pagination.count', { first: firstRecord, last: lastRecord, total }) }}
    </span>
    <label>
      <span>{{ t('engineering.pagination.rows') }}</span>
      <select :value="pageSize" @change="setPageSize">
        <option v-for="size in pageSizeOptions" :key="size" :value="size">
          {{ size }}
        </option>
      </select>
    </label>
    <div class="pagination__pages">
      <button
        type="button"
        :disabled="page <= 1"
        :aria-label="t('engineering.pagination.previousAria')"
        @click="emit('update:page', page - 1)"
      >
        {{ t('engineering.pagination.previous') }}
      </button>
      <button
        v-for="item in pages"
        :key="item"
        type="button"
        :class="{ active: item === page }"
        :aria-current="item === page ? 'page' : undefined"
        @click="emit('update:page', item)"
      >
        {{ item }}
      </button>
      <button
        type="button"
        :disabled="page >= totalPages"
        :aria-label="t('engineering.pagination.nextAria')"
        @click="emit('update:page', page + 1)"
      >
        {{ t('engineering.pagination.next') }}
      </button>
    </div>
  </nav>
</template>

<style scoped>
.pagination {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 14px;
  border-top: 1px solid #e1e6e4;
  background: #ffffff;
}

.pagination__count {
  margin-right: auto;
  color: #66726e;
  font-size: 12px;
}

label {
  display: flex;
  gap: 7px;
  align-items: center;
  color: #586660;
  font-size: 12px;
}

select,
button {
  min-height: 32px;
  padding: 5px 8px;
  border: 1px solid #adb9b4;
  border-radius: 8px;
  color: #17201d;
  background: #ffffff;
}

.pagination__pages {
  display: flex;
  gap: 4px;
}

button {
  cursor: pointer;
}

button.active {
  border-color: #3568a8;
  color: #ffffff;
  background: #3568a8;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

@media (max-width: 680px) {
  .pagination {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .pagination__count {
    width: 100%;
  }

  .pagination__pages {
    flex-wrap: wrap;
  }
}
</style>
